import * as XLSX from "xlsx";
import { ValidationResult, MaterialProviderRow, ValidationError } from "./types";

const validateThreshold = (value: string): number | null => {
    if (!value) return null;
    const cleaned = value.replace(/[^\d.]/g, "");
    return cleaned ? parseFloat(cleaned) : null;
};

const validateProviderPrice = (value: string): number => {
    if (!value) return 1; // Default to 1 if not provided

    let cleaned = value.replace(/[^\d\.,-]/g, "");

    if (cleaned.includes(".") && cleaned.includes(",")) {
        cleaned = cleaned.replace(/,/g, "");
    } else {
        cleaned = cleaned.replace(/,/g, ".");
    }

    const parsed = parseFloat(cleaned);

    if (isNaN(parsed)) {
        const digits = value.replace(/\D/g, "");
        return digits ? parseInt(digits, 10) : 1;
    }

    return Math.max(1, Math.round(parsed));
};

const normalizeCategory = (value: string): string => {
    if (!value) return "Vật tư chính";
    const normalized = value.trim().toUpperCase();
    
    const categoryMapping: { [key: string]: string } = {
        "NLVC": "Vật tư chính",
        "NLVP": "Vật tư phụ",
    };
    
    if (normalized in categoryMapping) {
        return categoryMapping[normalized];
    }
    
    // If already full name, return as-is
    if (normalized === "VẬT TƯ CHÍNH" || normalized === "VẬT TƯ PHỤ") {
        return normalized === "VẬT TƯ CHÍNH" ? "Vật tư chính" : "Vật tư phụ";
    }
    
    return "Vật tư chính";
};

const normalizeMeasurementUnit = (value: string): string => {
    if (!value) return "";
    const normalized = value.toLowerCase().trim();
    
    // Valid measurement units from config
    const validUnits: { [key: string]: string } = {
        "mét": "mét",
        "kg": "kg",
        "kilogram": "kg",
        "lít": "lít",
        "cái": "cái",
        "tấm": "tấm",
        "chai": "chai",
        "bộ": "bộ",
        "bịch": "bịch",
        "cuôn": "cuôn",
        "cuộn": "cuôn",
        "cây": "cây",
        "con": "con",
        "miếng": "miếng",
        "tờ": "tờ",
        "tuýt": "tuýt",
        "hộp": "hộp",
    };
    
    if (normalized in validUnits) {
        return validUnits[normalized];
    }
    
    return normalized;
};

export const validateExcelFile = async (file: File): Promise<ValidationResult> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: "array" });
                
                // Get first sheet name from SheetNames array (should be in file order)
                // If SheetNames might not be in order, try to get by workbook.Workbook?.Sheets order
                let sheetName: string;
                if (workbook.SheetNames && workbook.SheetNames.length > 0) {
                    sheetName = workbook.SheetNames[0];
                } else {
                    // Fallback: get first sheet from Sheets object
                    const sheetKeys = Object.keys(workbook.Sheets);
                    sheetName = sheetKeys[0] || "";
                }
                
                if (!sheetName || !workbook.Sheets[sheetName]) {
                    reject(new Error(`Không tìm thấy sheet đầu tiên. Các sheet có sẵn: ${workbook.SheetNames.join(", ")}`));
                    return;
                }
                
                const worksheet = workbook.Sheets[sheetName];
                const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });

                if (jsonData.length < 3) {
                    reject(new Error("File phải có ít nhất 3 dòng (headers ở dòng 3)"));
                    return;
                }

                // Get headers from row 3 (index 2)
                const headerRow = jsonData[2];
                if (!headerRow || headerRow.length === 0) {
                    reject(new Error("Không tìm thấy headers ở dòng 3"));
                    return;
                }

                // Map headers to field names (support Vietnamese and English)
                const headerMapping: { [key: string]: string } = {
                    // Material code
                    "mã vật tư": "material_code",
                    "material_code": "material_code",
                    "ma vat tu": "material_code",
                    // Material name
                    "tên vật tư": "name",
                    "name": "name",
                    "ten vat tu": "name",
                    // Storage
                    "kho số": "storage",
                    "storage": "storage",
                    "kho so": "storage",
                    // Category
                    "hạng mục": "category",
                    "category": "category",
                    "hang muc": "category",
                    // Measurement unit
                    "đơn vị": "measurement_unit",
                    "measurement_unit": "measurement_unit",
                    "don vi": "measurement_unit",
                    // Threshold
                    "mức cảnh bảo tồn kho": "threshold",
                    "threshold": "threshold",
                    "muc canh bao ton kho": "threshold",
                    // Note
                    "ghi chú": "note",
                    "note": "note",
                    "ghi chu": "note",
                    // Provider code
                    "mã ncc": "provider_code",
                    "provider_code": "provider_code",
                    "ma ncc": "provider_code",
                    // Provider name
                    "tên ncc": "provider_name",
                    "provider_name": "provider_name",
                    "ten ncc": "provider_name",
                    // Provider price
                    "giá nhập": "provider_price",
                    "provider_price": "provider_price",
                    "gia nhap": "provider_price",
                };

                const fieldMap: { [key: string]: number } = {};

                headerRow.forEach((header: any, colIdx: number) => {
                    if (header) {
                        const headerStr = String(header).trim().toLowerCase();
                        if (headerStr in headerMapping) {
                            fieldMap[headerMapping[headerStr]] = colIdx;
                        }
                    }
                });

                const rows: MaterialProviderRow[] = [];
                const errors: ValidationError[] = [];
                let originalRowCount = 0;

                // Process data starting from row 4 (index 3)
                for (let rowIdx = 3; rowIdx < jsonData.length; rowIdx++) {
                    const row = jsonData[rowIdx];
                    const rowNumber = rowIdx + 1; // Excel row number (1-based)

                    // Skip empty rows
                    if (!row || row.every((cell: any) => cell === null || cell === undefined || String(cell).trim() === "")) {
                        continue;
                    }
                    
                    originalRowCount++;

                    const rowErrors: ValidationError[] = [];
                    const rowWarnings: ValidationError[] = [];
                    const materialProviderData: any = {};

                    // Process fields
                    Object.entries(fieldMap).forEach(([field, colIdx]) => {
                        try {
                            let value = row[colIdx];
                            if (value === null || value === undefined) {
                                return;
                            }

                            if (typeof value !== "string") {
                                value = String(value);
                            }

                            // Apply field-specific validation and normalization
                            if (field === "material_code" || field === "name" || field === "note" || 
                                field === "provider_code" || field === "provider_name" || field === "storage") {
                                value = value.replace(/\s+/g, " ").trim();
                                materialProviderData[field] = value;
                            } else if (field === "category") {
                                materialProviderData[field] = normalizeCategory(value);
                            } else if (field === "measurement_unit") {
                                materialProviderData[field] = normalizeMeasurementUnit(value);
                            } else if (field === "threshold") {
                                const thresholdValue = validateThreshold(value);
                                if (thresholdValue !== null) {
                                    materialProviderData[field] = thresholdValue;
                                }
                            } else if (field === "provider_price") {
                                materialProviderData[field] = validateProviderPrice(value);
                            } else {
                                materialProviderData[field] = value;
                            }
                        } catch (e: any) {
                            rowErrors.push({
                                row: rowNumber,
                                field,
                                error: `Lỗi xử lý trường: ${e.message}`,
                                data: row[colIdx],
                            });
                        }
                    });

                    // Validate required fields
                    if (!materialProviderData.material_code || String(materialProviderData.material_code).trim() === "") {
                        rowErrors.push({
                            row: rowNumber,
                            field: "material_code",
                            error: "Mã vật tư là bắt buộc",
                        });
                    }

                    if (!materialProviderData.name || String(materialProviderData.name).trim() === "") {
                        rowErrors.push({
                            row: rowNumber,
                            field: "name",
                            error: "Tên vật tư là bắt buộc",
                        });
                    }

                    // Provider code/name is optional - warn if missing
                    if (!materialProviderData.provider_code && !materialProviderData.provider_name) {
                        rowWarnings.push({
                            row: rowNumber,
                            field: "provider_code",
                            error: "Cảnh báo: Không có mã/tên nhà cung cấp. Vật tư sẽ được tạo mà không có quan hệ nhà cung cấp.",
                        });
                    }

                    // Provider price defaults to 1 only if provider_code exists and price is invalid
                    if ((materialProviderData.provider_code || materialProviderData.provider_name) && 
                        (!materialProviderData.provider_price || materialProviderData.provider_price <= 0)) {
                        rowWarnings.push({
                            row: rowNumber,
                            field: "provider_price",
                            error: "Cảnh báo: Giá nhập không hợp lệ hoặc không có. Sẽ đặt mặc định là 1.",
                        });
                        materialProviderData.provider_price = 1;
                    }

                    // Set defaults
                    if (!materialProviderData.storage) {
                        materialProviderData.storage = "Kho 1";
                    }
                    if (!materialProviderData.category) {
                        materialProviderData.category = "Vật tư chính";
                    }

                    const rowToAdd: MaterialProviderRow = {
                        rowNumber,
                        data: materialProviderData,
                        isValid: rowErrors.length === 0,
                        errors: [...rowErrors, ...rowWarnings],
                    };

                    rows.push(rowToAdd);
                    errors.push(...rowErrors);
                }

                resolve({ rows, errors, originalRowCount });
            } catch (e: any) {
                reject(new Error(`Lỗi đọc file: ${e.message}`));
            }
        };

        reader.onerror = () => {
            reject(new Error("Không thể đọc file"));
        };

        reader.readAsArrayBuffer(file);
    });
};

